import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { waLink } from '../data/contact';
import { short, sipValue } from '../utils/money';
import Reveal from '../components/layout/Reveal';
import { Breadcrumb, usePageTitle } from '../components/layout/PageBits';
import { IMG } from '../data/media';

const GOAL_IMG = [IMG.goalEducation, IMG.goalRetirement, IMG.goalHome, IMG.goalMarriage];

const C = {
  en: {
    kick: 'Mutual funds & SIP', title: 'Grow your savings, one month at a time.',
    sub: 'We help you pick funds for each family goal and set up a SIP you can afford. Every number here is an illustration, never a promise.',
    cta: 'Start a SIP on WhatsApp', dash: 'Explore the fund dashboard',
    phoneTitle: 'My SIP', phoneGoal: "Daughter's college", phoneMonthly: '₹5,000 a month',
    goalsTitle: 'Pick a goal', goalSip: 'A SIP of', goalFor: 'for', goalCould: 'could grow to about',
    goals: [['🎓', "Child's education", 15, 5000], ['🌅', 'Retirement', 25, 10000], ['🏡', 'New home', 10, 15000], ['💍', "Child's marriage", 18, 4000]],
    vsTitle: 'SIP or lump sum?',
    sip: ['SIP', 'A fixed amount every month', 'Good for monthly earners', 'Averages out the price you pay', 'Start from ₹500'],
    lump: ['Lump sum', 'One larger amount at once', 'Good for a bonus or a matured policy', 'Timing matters more', 'Can be split into a SIP (STP)'],
    catTitle: 'Types of funds, in plain words',
    cats: [['Equity funds', 'Invest in company shares. For goals 5+ years away.', 'High'], ['Debt funds', 'Lend to government and companies. For stability.', 'Low to moderate'], ['Hybrid funds', 'A mix of shares and debt.', 'Moderate'], ['ELSS (tax saving)', 'Equity funds with a tax benefit under 80C; 3-year lock-in.', 'High'], ['Index funds', 'Follow a market index such as the Nifty 50, at low cost.', 'High']],
    risk: 'Risk', toolsTitle: 'Tools', calc: 'All calculators', compare: 'Compare funds',
  },
  bn: {
    kick: 'মিউচুয়াল ফান্ড ও SIP', title: 'মাসে মাসে সঞ্চয় বাড়ান।',
    sub: 'পরিবারের প্রতিটি লক্ষ্যের জন্য ফান্ড বেছে নিতে আর সাধ্যমতো SIP শুরু করতে আমরা সাহায্য করি। এখানের সব সংখ্যা উদাহরণ, প্রতিশ্রুতি নয়।',
    cta: 'হোয়াটসঅ্যাপে SIP শুরু করুন', dash: 'ফান্ড ড্যাশবোর্ড দেখুন',
    phoneTitle: 'আমার SIP', phoneGoal: 'মেয়ের কলেজ', phoneMonthly: 'মাসে ₹৫,০০০',
    goalsTitle: 'একটি লক্ষ্য বেছে নিন', goalSip: 'মাসে', goalFor: 'করে', goalCould: 'হতে পারে প্রায়',
    goals: [['🎓', 'সন্তানের পড়াশোনা', 15, 5000], ['🌅', 'অবসর', 25, 10000], ['🏡', 'নতুন বাড়ি', 10, 15000], ['💍', 'সন্তানের বিয়ে', 18, 4000]],
    vsTitle: 'SIP না এককালীন?',
    sip: ['SIP', 'প্রতি মাসে নির্দিষ্ট টাকা', 'মাসিক আয়ের মানুষের জন্য ভালো', 'কেনার দাম গড় হয়ে যায়', '₹৫০০ থেকে শুরু'],
    lump: ['এককালীন', 'একবারে বড় অঙ্ক', 'বোনাস বা ম্যাচিওর হওয়া পলিসির টাকার জন্য', 'সময় বেশি গুরুত্বপূর্ণ', 'SIP-এর মতো ভাগ করা যায় (STP)'],
    catTitle: 'ফান্ডের ধরন, সহজ ভাষায়',
    cats: [['ইক্যুইটি ফান্ড', 'কোম্পানির শেয়ারে বিনিয়োগ। ৫+ বছরের লক্ষ্যের জন্য।', 'বেশি'], ['ডেট ফান্ড', 'সরকার ও কোম্পানিকে ধার। স্থিতির জন্য।', 'কম থেকে মাঝারি'], ['হাইব্রিড ফান্ড', 'শেয়ার ও ডেটের মিশ্রণ।', 'মাঝারি'], ['ELSS (কর সাশ্রয়)', '৮০C-তে কর ছাড়সহ ইক্যুইটি ফান্ড; ৩ বছরের লক-ইন।', 'বেশি'], ['ইনডেক্স ফান্ড', 'নিফটি ৫০-এর মতো সূচক অনুসরণ করে, কম খরচে।', 'বেশি']],
    risk: 'ঝুঁকি', toolsTitle: 'টুলস', calc: 'সব ক্যালকুলেটর', compare: 'ফান্ড তুলনা',
  },
};
const RISK_CLS = ['bg-rose-100 text-rose-700', 'bg-sky text-brand', 'bg-amber-100 text-amber-800', 'bg-rose-100 text-rose-700', 'bg-rose-100 text-rose-700'];

function InvestmentsPage() {
  const { lang, t } = useLang();
  const c = C[lang];
  const [goal, setGoal] = useState(0);
  usePageTitle(c.kick);
  const [, , years, monthly] = c.goals[goal];
  const value = sipValue(monthly, 12, years);

  return (
    <>
      <section className="bg-gradient-to-br from-sky via-white to-mint overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Breadcrumb current={c.kick} />
            <p className="kicker text-emerald-700">{c.kick}</p>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">{c.title}</h1>
            <p className="mt-4 text-lg text-slate-600">{c.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={waLink(t('waLead')(c.kick, ''))} target="_blank" rel="noopener noreferrer" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-3 rounded-full">💬 {c.cta}</a>
              <Link to="/mutual-funds" className="bg-white border border-slate-200 font-bold px-5 py-3 rounded-full">{c.dash} →</Link>
            </div>
            <p className="mt-6 text-xs text-slate-500">{t('mfRisk')}</p>
          </Reveal>
          {/* Phone mock-up with an illustrative SIP card */}
          <Reveal className="relative flex justify-center">
            <div className="absolute w-72 h-72 rounded-full bg-emerald-200/50 blur-3xl" aria-hidden="true" />
            <div className="relative w-64 sm:w-72 rounded-[2.5rem] bg-ink p-3 shadow-2xl" aria-hidden="true">
              <div className="rounded-[2rem] bg-white overflow-hidden">
                <div className="h-6 flex justify-center items-center"><span className="w-16 h-1.5 rounded-full bg-slate-200" /></div>
                <div className="px-5 pb-6">
                  <div className="text-xs text-slate-500">{c.phoneTitle}</div>
                  <div className="font-extrabold text-lg">{c.phoneGoal}</div>
                  <div className="text-sm text-emerald-600 font-semibold">{c.phoneMonthly}</div>
                  <svg viewBox="0 0 200 90" className="w-full mt-4"><defs><linearGradient id="kc-ig" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#10b981" stopOpacity=".35" /><stop offset="1" stopColor="#10b981" stopOpacity="0" /></linearGradient></defs>
                    <path d="M0 80 C30 76 50 70 80 60 S140 34 170 20 S195 8 200 6 V90 H0Z" fill="url(#kc-ig)" /><path d="M0 80 C30 76 50 70 80 60 S140 34 170 20 S195 8 200 6" fill="none" stroke="#10b981" strokeWidth="3" /></svg>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-xl bg-slate-50 p-2"><div className="text-slate-400">{t('sipInv')}</div><div className="font-bold">{short(5000 * 12 * 15, lang)}</div></div>
                    <div className="rounded-xl bg-mint p-2"><div className="text-slate-400">{t('sipVal')}</div><div className="font-bold text-emerald-700">{short(sipValue(5000, 12, 15), lang)}</div></div>
                  </div>
                  <div className="mt-3 text-[10px] text-slate-400">{t('illus12')}</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 mb-8">{c.goalsTitle}</h2></Reveal>
          <Reveal className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {c.goals.map(([, name], i) => (
              <button key={name} type="button" aria-pressed={goal === i} onClick={() => setGoal(i)}
                className={`rounded-2xl p-4 text-left border-2 transition ${goal === i ? 'border-emerald-500 bg-mint' : 'border-slate-200 hover:border-slate-300'}`}>
                <img src={GOAL_IMG[i]} alt="" aria-hidden="true" loading="lazy" className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-md" /><div className="mt-2 font-bold">{name}</div>
              </button>
            ))}
          </Reveal>
          <Reveal className="mt-6 rounded-3xl bg-ink text-white p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-lg sm:text-xl leading-relaxed">
              <img src={GOAL_IMG[goal]} alt="" aria-hidden="true" className="inline-block w-10 h-10 mr-2 -mt-1 object-contain" />{c.goalSip} <b>₹{monthly.toLocaleString('en-IN')}</b> {c.goalFor} <b>{years} {t('yrs')}</b> {c.goalCould} <b className="text-amber-200">{short(value, lang)}</b>.
              <div className="text-xs text-white/60 mt-2">{t('illus12')}</div>
            </div>
            <Link to="/calculators" className="shrink-0 bg-white text-ink font-bold px-5 py-3 rounded-full text-center">{c.calc} →</Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 text-center mb-10">{c.vsTitle}</h2></Reveal>
          <Reveal className="grid md:grid-cols-2 gap-5">
            {[c.sip, c.lump].map(([h, ...pts], i) => (
              <div key={h} className={`rounded-3xl p-6 sm:p-8 ${i === 0 ? 'bg-white shadow-lg' : 'bg-white/60 border border-amber-200'}`}>
                <h3 className={`text-2xl font-extrabold ${i === 0 ? 'text-emerald-700' : 'text-saffron'}`}>{h}</h3>
                <ul className="mt-4 space-y-2.5">{pts.map((p) => <li key={p} className="flex gap-3 text-slate-700"><span aria-hidden="true">•</span>{p}</li>)}</ul>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 mb-8">{c.catTitle}</h2></Reveal>
          <Reveal className="divide-y divide-slate-200 border-y border-slate-200">
            {c.cats.map(([name, desc, risk], i) => (
              <div key={name} className="py-5 grid sm:grid-cols-[1fr_2fr_11rem] gap-2 sm:gap-6 sm:items-center">
                <div className="font-bold text-lg">{name}</div>
                <div className="text-slate-600">{desc}</div>
                <span className={`justify-self-start sm:justify-self-end text-xs font-bold rounded-full px-3 py-1 ${RISK_CLS[i]}`}>{c.risk}: {risk}</span>
              </div>
            ))}
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/mutual-funds" className="bg-sky text-brand font-semibold px-4 py-2.5 rounded-xl">{c.dash} →</Link>
            <Link to="/compare" className="bg-sky text-brand font-semibold px-4 py-2.5 rounded-xl">{c.compare} →</Link>
            <Link to="/calculators" className="bg-sky text-brand font-semibold px-4 py-2.5 rounded-xl">{c.calc} →</Link>
          </div>
          <p className="mt-6 text-xs text-slate-500">{t('mfRisk')}</p>
        </div>
      </section>
    </>
  );
}

export default InvestmentsPage;
